import { useState, type FormEvent } from 'react';
import { Megaphone, Send } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import type {
  BroadcastAudienceType,
  BroadcastResult,
  MessageChannel,
} from '@/types/messaging';
import { notificationsApi } from '@/features/institute/notifications/api/notifications.api';
import { RecipientPicker } from './RecipientPicker';

/**
 * Writing one message and sending it to everybody currently on the roll.
 *
 * Two things this screen has to be honest about, because neither can be undone:
 *
 *   how many will receive it   read from the roll before a word is typed, so
 *                              "Send" is never a guess about who is listening
 *   who will not               a student with no mobile number recorded is
 *                              named, not silently dropped
 *
 * The confirmation step is deliberate. Every other Save in this product is
 * reversible by editing the record again; this one arrives on two hundred
 * handsets, costs the institute money, and cannot be recalled.
 */

/** Matches `MESSAGE_MAX_LENGTH` on the server, which is the one that decides. */
const MESSAGE_MAX_LENGTH = 480;

/** One SMS is 160 characters; past that the institute is billed per part. */
const SMS_PART_LENGTH = 160;

/**
 * Every channel this screen knows how to compose for.
 *
 * Used only when the server reports that none are configured, so the screen can
 * still be shown and walked through before an administrator adds the sender
 * credentials. Nothing goes out in that state — the server refuses a broadcast
 * it has no sender for — and the screen says so rather than implying a send.
 */
const ALL_CHANNELS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

export interface SendMessageModalProps {
  onClose: () => void;
}

export function SendMessageModal({ onClose }: SendMessageModalProps) {
  const {
    data: audience,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => notificationsApi.audience(), []);

  const [channel, setChannel] = useState<MessageChannel>('SMS');
  const [message, setMessage] = useState('');

  /**
   * Who it goes to. Everybody by default, which is what this screen has always
   * done — picking a few is the deliberate narrowing, not the other way round.
   */
  const [audienceKind, setAudienceKind] = useState<BroadcastAudienceType>('ALL_MEMBERS');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const configuredChannels = [
    ...(audience?.channels.sms ? [{ value: 'SMS', label: 'SMS' }] : []),
    ...(audience?.channels.whatsapp ? [{ value: 'WHATSAPP', label: 'WhatsApp' }] : []),
  ];

  /** False until an administrator adds the sender credentials on the server. */
  const isConfigured = configuredChannels.length > 0;
  const channelOptions = isConfigured ? configuredChannels : ALL_CHANNELS;

  /**
   * The channel actually in force.
   *
   * Derived rather than stored, because the state is initialised before the
   * audience arrives: an installation with WhatsApp but no SMS sender would
   * otherwise show "WhatsApp" in the dropdown while the state still said SMS,
   * and the send would be refused by a server that is configured correctly.
   */
  const available = channelOptions.map((option) => option.value);
  const activeChannel = (
    available.includes(channel) ? channel : (available[0] ?? 'SMS')
  ) as MessageChannel;

  const isSelection = audienceKind === 'INDIVIDUAL';

  /**
   * How many will actually receive it, as the screen currently stands.
   *
   * Only the reachable can be ticked, so a selection's size *is* its recipient
   * count — there is no second filter to apply on top of it here.
   */
  const recipientCount = isSelection ? chosen.size : (audience?.reachable ?? 0);

  /**
   * Whether this institute has anybody reachable at all — which is what decides
   * if the send button is offered, rather than the current selection.
   *
   * An empty selection disables the button instead of removing it: it is one
   * tick away from being usable, and a control that disappears as the audience
   * is switched reads as something breaking.
   */
  const canSend = (audience?.reachable ?? 0) > 0;
  const parts = Math.max(1, Math.ceil(message.length / SMS_PART_LENGTH));

  function toggleChosen(uuid: string) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  function setManyChosen(uuids: string[], next: boolean) {
    setChosen((current) => {
      const updated = new Set(current);
      for (const uuid of uuids) {
        if (next) updated.add(uuid);
        else updated.delete(uuid);
      }
      return updated;
    });
  }

  function review(event: FormEvent) {
    event.preventDefault();
    setIsConfirming(true);
  }

  async function send() {
    setIsSending(true);

    try {
      const sent = await notificationsApi.send({
        channel: activeChannel,
        message: message.trim(),
        audienceType: audienceKind,
        // This popup writes to the families on the roll, which is what it has
        // always done — the four "Include" toggles belong to the notifications
        // screen, and are fixed here rather than left for the caller to guess.
        includeStudents: false,
        includeParents: true,
        includeTeachers: false,
        includeStaff: false,
        // Sent only with a selection — the server refuses a list alongside
        // "everybody", rather than quietly widening the send.
        ...(isSelection && { studentUuids: [...chosen] }),
      });
      setResult(sent);
      setIsConfirming(false);

      if (sent.failed === 0) {
        toast.success(
          `Sent to ${sent.sent} ${sent.sent === 1 ? 'person' : 'people'}`,
          sent.skipped > 0
            ? `${sent.skipped} had no mobile number on file.`
            : 'Everyone was reached.',
        );
      } else {
        toast.error(
          `${sent.failed} could not be reached`,
          `${sent.sent} went out. The ones that failed are listed.`,
        );
      }
    } catch (caught) {
      // The server's messages name the reason — no credentials, nobody with a
      // number — so they are worth showing verbatim.
      toast.error(
        'Could not send that notification',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
      setIsConfirming(false);
    } finally {
      setIsSending(false);
    }
  }

  // --- what happened, once it has been sent --------------------------------

  if (result) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Notification sent"
        size="sm"
        footer={
          <Button variant="primary" size="sm" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="success">{result.sent} sent</Badge>
            {result.failed > 0 && <Badge tone="critical">{result.failed} failed</Badge>}
            {result.skipped > 0 && <Badge tone="warning">{result.skipped} no number</Badge>}
          </div>

          {result.failures.length > 0 && (
            <div className="rounded-card border border-line">
              <p className="border-b border-line bg-surface-2 px-3 py-2 lbl">
                Could not be reached
              </p>
              <ul className="divide-y divide-line">
                {result.failures.map((failure) => (
                  <li key={`${failure.name}-${failure.mobile}`} className="px-3 py-2">
                    <p className="text-sm text-ink">{failure.name}</p>
                    <p className="font-mono text-xs tabular-nums text-ink-3">{failure.mobile}</p>
                    <p className="text-xs text-crit">{failure.reason}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.unreachable.length > 0 && (
            <Alert tone="warning" title={`${result.unreachable.length} have no mobile number`}>
              {result.unreachable
                .slice(0, 5)
                .map((student) => student.name)
                .join(', ')}
              {result.unreachable.length > 5 && ` and ${result.unreachable.length - 5} more`}. Add a
              number to their record so they hear about the next one.
            </Alert>
          )}
        </div>
      </Modal>
    );
  }

  // --- confirming ----------------------------------------------------------

  if (isConfirming) {
    return (
      <Modal
        isOpen
        onClose={() => setIsConfirming(false)}
        title={isSelection ? `Send this to ${recipientCount}?` : 'Send this to everyone?'}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsConfirming(false)}
              disabled={isSending}
            >
              Back
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSending}
              leftIcon={<Send className="h-4 w-4" />}
              onClick={() => void send()}
            >
              {isSending ? 'Sending…' : `Send to ${recipientCount}`}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {isConfigured ? (
            <Alert tone="warning" title="This cannot be undone">
              {recipientCount} {recipientCount === 1 ? 'person' : 'people'}{' '}
              {isSelection ? 'you chose will receive' : 'will receive'} this by{' '}
              {activeChannel === 'SMS' ? 'SMS' : 'WhatsApp'}
              {activeChannel === 'SMS' && parts > 1
                ? `, billed as ${parts} notifications each`
                : ''}
              . There is no way to recall it.
            </Alert>
          ) : (
            <Alert tone="warning" title="Nothing will be delivered">
              No sender is configured on this installation, so this send will be refused by the
              server. Once an administrator adds the credentials it would reach{' '}
              {recipientCount} {recipientCount === 1 ? 'person' : 'people'}.
            </Alert>
          )}

          <div className="rounded-card border border-line bg-surface-2 p-3">
            <p className="lbl">What they will read</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{message.trim()}</p>
          </div>
        </div>
      </Modal>
    );
  }

  // --- composing -----------------------------------------------------------

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Send a notification"
      description={
        isSelection
          ? 'Goes to the students you choose from the roll.'
          : 'Goes to every student currently on the roll.'
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {canSend ? 'Cancel' : 'Close'}
          </Button>
          {/* Offered only when there is a channel and somebody to reach. A
              permanently disabled button invites people to keep clicking it. */}
          {canSend && (
            <Button
              variant="primary"
              size="sm"
              form="send-notification-form"
              type="submit"
              disabled={message.trim().length === 0 || recipientCount === 0}
              leftIcon={<Send className="h-4 w-4" />}
            >
              {isSelection && recipientCount === 0
                ? 'Choose who to send to'
                : `Review and send${recipientCount > 0 ? ` to ${recipientCount}` : ''}`}
            </Button>
          )}
        </>
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-3" role="status" aria-label="Loading the audience">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : error || !audience ? (
        <Alert tone="critical" title="Could not work out who this would go to">
          {error?.message ?? 'Please try again.'}{' '}
          <button type="button" onClick={refetch} className="font-[560] underline">
            Try again
          </button>
        </Alert>
      ) : (
        <form id="send-notification-form" onSubmit={review} className="flex flex-col gap-4">
          {/* The form is shown either way — an empty screen cannot be reviewed —
              but it never pretends a send would leave the building. */}
          {!isConfigured && (
            <Alert tone="warning" title="Notifications are not set up yet">
              This installation has no SMS or WhatsApp sender configured, so nothing will actually
              be delivered. An administrator adds the notification service credentials on the
              server; once they have, this screen works without any further change.
            </Alert>
          )}

          {audience.reachable === 0 && (
            <Alert tone="warning" title="There is nobody to send to">
              No student currently on the roll has a mobile number recorded.
            </Alert>
          )}

          {/*
            Who it goes to and how, on one line.

            Both choices are visible without opening anything, and the counts sit
            in the options themselves rather than in a separate row of badges —
            the dialog is wide, so the space to spend is sideways, not downwards.
          */}
          {audience.reachable > 0 && (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,11rem)] sm:items-end">
              <fieldset className="min-w-0">
                <legend className="lbl mb-1.5">Send to</legend>

                <div className="flex flex-col sm:flex-row gap-2">
                  {(
                    [
                      {
                        value: 'ALL_MEMBERS' as const,
                        label: 'Everyone on the roll',
                        count: audience.reachable,
                      },
                      {
                        value: 'INDIVIDUAL' as const,
                        label: 'Students I choose',
                        count: chosen.size,
                      },
                    ]
                  ).map((option) => (
                    <label
                      key={option.value}
                      className={cn(
                        'flex h-11 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-field border px-3',
                        'transition-colors duration-150',
                        audienceKind === option.value
                          ? 'border-accent bg-accent-soft'
                          : 'border-line hover:border-line-strong',
                      )}
                    >
                      <input
                        type="radio"
                        name="notification-audience"
                        className="h-4 w-4 shrink-0 accent-accent"
                        checked={audienceKind === option.value}
                        onChange={() => setAudienceKind(option.value)}
                      />
                      <span className="min-w-0 flex-1 truncate text-sm text-ink">
                        {option.label}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 rounded-pill px-2 py-0.5 text-2xs font-[650] tabular-nums',
                          audienceKind === option.value
                            ? 'bg-accent text-surface'
                            : 'bg-surface-2 text-ink-3',
                        )}
                      >
                        {option.count}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <Select
                label="Send by"
                options={channelOptions}
                disabled={channelOptions.length < 2}
                value={activeChannel}
                onChange={(event) => setChannel(event.target.value as MessageChannel)}
              />
            </div>
          )}

          {/*
            The roll beside the message rather than above it: picking people and
            writing to them are one job, and stacking them made a dialog nobody
            could see the end of.
          */}
          <div className={cn('grid gap-4', isSelection && 'lg:grid-cols-2')}>
            {isSelection && (
              <RecipientPicker
                students={audience.students}
                selected={chosen}
                onToggle={toggleChosen}
                onSelectAll={setManyChosen}
              />
            )}

            <div className="flex min-w-0 flex-col gap-2">
              <Textarea
                label="Notification"
                rows={isSelection ? 7 : 4}
                maxLength={MESSAGE_MAX_LENGTH}
                placeholder="Fees for this term are due on 25 August. Please pay at the office."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                hint={
                  activeChannel === 'SMS'
                    ? `${message.length}/${MESSAGE_MAX_LENGTH} characters · billed as ${parts} ${parts === 1 ? 'notification' : 'notifications'} each`
                    : `${message.length}/${MESSAGE_MAX_LENGTH} characters`
                }
              />

              <p className="text-xs text-ink-3">
                Recipients see the institute's number, not yours, and cannot reply. Put the office
                number in the text if you want an answer.
                {audience.unreachable > 0 &&
                  ` ${audience.unreachable} on the roll have no number recorded and cannot be reached.`}
              </p>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}

/**
 * The button that opens the popup.
 *
 * Kept beside the modal rather than in the layout, so the two travel together —
 * a second place that needs to offer this imports one thing, not two.
 *
 * The one solid button in a bar of quiet icons, and deliberately so: it is the
 * only *action* up there — the bell and the theme toggle change what you are
 * looking at, this one reaches two hundred handsets. A megaphone rather than a
 * chat bubble, because it announces to everybody rather than opening a
 * conversation with somebody.
 *
 * The label collapses below `sm`, leaving a round accent button that still
 * carries its name for a screen reader.
 */
export function SendMessageButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label="Send a notification to the roll"
        onClick={() => setIsOpen(true)}
        className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-extrabold px-3.5 py-2 shadow-md shadow-blue-500/25 border border-blue-400/30 transition-all cursor-pointer inline-flex items-center gap-2 text-xs sm:text-sm"
      >
        <Megaphone className="h-4 w-4 shrink-0 text-white" strokeWidth={2.2} />
        <span className="hidden sm:inline">Send notification</span>
      </button>

      {isOpen && <SendMessageModal onClose={() => setIsOpen(false)} />}
    </>
  );
}
